import { useState } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EditReadingDialog } from "./EditReadingDialog";
import { DeleteReadingDialog } from "./DeleteReadingDialog";

interface MonitoringReadingsTableProps {
  profile: any;
  readings: any[];
}

export function MonitoringReadingsTable({ profile, readings }: MonitoringReadingsTableProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const parameters = (profile.parameters as any[]) || [];
  
  const [editingReading, setEditingReading] = useState<any>(null);
  const [deletingReading, setDeletingReading] = useState<any>(null);

  const deleteMutation = useMutation({
    mutationFn: async (readingId: string) => {
      const { error } = await supabase
        .from("special_monitoring_readings")
        .delete()
        .eq("id", readingId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Reading deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["special-monitoring-readings"] });
      setDeletingReading(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete reading: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  if (readings.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reading History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No readings recorded yet. Click "Take Reading" to start tracking.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reading History ({readings.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                {parameters.map((param: any) => (
                  <TableHead key={param.name}>{param.name}</TableHead>
                ))}
                <TableHead>Alerts</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {readings.map((reading) => {
                const readingData = reading.readings as any[];
                const hasAlerts = reading.alerts_triggered && (reading.alerts_triggered as any[]).length > 0;
                
                return (
                  <TableRow key={reading.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(reading.recorded_at), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    {parameters.map((param: any) => {
                      const value = readingData?.find((r: any) => r.name === param.name);
                      const isOutOfRange = value && (value.value < param.min || value.value > param.max);
                      
                      return (
                        <TableCell key={param.name}>
                          <span className={isOutOfRange ? "text-destructive font-semibold" : ""}>
                            {value?.value || '-'} {value?.unit || param.unit}
                          </span>
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      {hasAlerts ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {(reading.alerts_triggered as any[]).length}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">None</Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {reading.notes || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingReading(reading)}
                          title="Edit reading"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeletingReading(reading)}
                          title="Delete reading"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {editingReading && (
        <EditReadingDialog
          reading={editingReading}
          profile={profile}
          open={!!editingReading}
          onOpenChange={(open) => !open && setEditingReading(null)}
        />
      )}

      {deletingReading && (
        <DeleteReadingDialog
          open={!!deletingReading}
          onOpenChange={(open) => !open && setDeletingReading(null)}
          onConfirm={() => deleteMutation.mutate(deletingReading.id)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </Card>
  );
}
